<template>
	<div class="d-flex w-100 align-center ga-2" @click.stop>
		<v-form ref="form" class="flex-grow-1" @submit.prevent="save">
			<!-- No server-unit backstop needed (LSP-16854/§3.3): FIELD_LENGTHS.TEXT is 125 code points, and a
			     code point is at most 2 UTF-16 units, so this field can never exceed 250 UTF-16 units — under
			     contact-api's validateChecklistName (checklists.ts:433), which rejects at UTF-16 255. If this
			     cap is ever raised above 127, add VuetifyValidation.ServerUtf16BackstopValidation
			     (src/helpers/Validation.ts) as a rule here. -->
			<CharacterLimitedTextField
				ref="nameInput"
				v-model="checklistName"
				field-noun="checklist name"
				hide-details="auto"
				class="mb-0"
				:max-length="FIELD_LENGTHS.TEXT"
				hard-cap
				:rules="[rules.required]"
				@keydown.esc.prevent="cancel">
				<template #label><FieldLabel label="Checklist Name (Required)" /></template>
			</CharacterLimitedTextField>
		</v-form>
		<!-- LSP-20478: regular-weight checkmark so it matches the stroke of its icon-close-medium
		     partner (icon-check-heavy is the heavy-stroke glyph and read as bolder). -->
		<v-btn class="align-self-start mt-1" variant="plain" icon="icon-check-big" :disabled="isSaving" @click.stop.prevent="save"> </v-btn>
		<v-btn class="align-self-start mt-1" variant="plain" icon="icon-close-medium" :disabled="isSaving" @click.stop.prevent="cancel"></v-btn>
	</div>
</template>

<script setup lang="ts">
	import { nextTick, onBeforeUnmount, ref, watch } from "vue";
	import api from "../api";
	import { FIELD_LENGTHS, VuetifyValidation } from "../helpers/Validation";
	import { getMessageFromError } from "../helpers/utilities";
	import { getApiErrorStatus, isEntityGoneError } from "../helpers/apiErrorShape";
	import { useToast } from "vue-toastification";
	import type { VForm } from "vuetify/components";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	// Type-only import (globally registered at runtime by main.ts — see CharacterLimitedTextField.vue's
	// header comment for why the template above doesn't import it locally) — this reference is erased
	// at compile time and exists only so `nameInput` below is typed as the field it actually refs.
	import type CharacterLimitedTextField from "@/components/formfields/CharacterLimitedTextField.vue";

	const props = defineProps<{
		checklistId: string;
		name: string;
		existingNames?: string[];
	}>();

	const emit = defineEmits<{
		(e: "saved", name: string): void;
		(e: "cancel"): void;
		(e: "update:isDirty", dirty: boolean): void;
		// LSP-20291: the checklist was already removed from the project elsewhere (e.g. on the iPad app).
		(e: "gone", error: unknown): void;
	}>();

	const toast = useToast();
	const checklistName = ref(props.name);
	const form = ref<InstanceType<typeof VForm> | null>(null);
	const nameInput = ref<InstanceType<typeof CharacterLimitedTextField> | null>(null);
	const isSaving = ref(false);
	// LSP-19790: id of the visible save-failure banner, so a repeated Save replaces it instead of
	// stacking another copy. Covers both ways a rename is refused — the local duplicate-name check
	// AND whatever the server says — matching ChecklistEditor, where the server path was the one the
	// bug was actually reported on. Dismissing an id that already expired is a no-op in
	// vue-toastification, so a stale value here is harmless.
	const saveErrorToastId = ref<number | string | null>(null);

	function dismissSaveErrorToast() {
		if (saveErrorToastId.value == null) return;
		toast.dismiss(saveErrorToastId.value);
		saveErrorToastId.value = null;
	}

	/** Show a save failure as the ONLY save-failure banner on screen. */
	function showSaveErrorToast(message: string) {
		dismissSaveErrorToast();
		saveErrorToastId.value = toast.error(message);
	}
	const rules = {
		required: (value: string) => {
			return VuetifyValidation.isValid(value, [VuetifyValidation.RequiredValidationField("Checklist Name is required.")]);
		}
	};

	watch(
		() => props.name,
		(newName, oldName) => {
			// A socket reload must not replace a rename still being edited locally.
			if (checklistName.value.trim() === oldName) checklistName.value = newName;
		}
	);

	watch([checklistName, () => props.name], ([name, savedName]) => emit("update:isDirty", name.trim() !== savedName));
	onBeforeUnmount(() => emit("update:isDirty", false));

	async function save(): Promise<boolean> {
		if (isSaving.value) {
			return false;
		}

		const trimmedName = checklistName.value.trim();

		if (trimmedName === props.name) {
			dismissSaveErrorToast();
			emit("cancel");
			return true;
		}
		if (props.existingNames?.includes(trimmedName)) {
			showSaveErrorToast(`A checklist with the name "${trimmedName}" already exists.`);
			return false;
		}
		// Past the name check: any standing banner describes an attempt the user has since changed.
		// If the request below fails, its own catch posts the replacement.
		dismissSaveErrorToast();

		const { valid } = (await form.value?.validate()) ?? { valid: false };
		if (!valid || !trimmedName || isSaving.value) {
			return false;
		}

		isSaving.value = true;
		try {
			await api.updateChecklistField(props.checklistId, { name: trimmedName });
			toast.success("Checklist name updated.");
			emit("saved", trimmedName);
			return true;
		} catch (error) {
			// LSP-20291: a 404/410 means another device already removed this checklist from the project.
			// That is not a failure the user can fix by retrying, so instead of an error toast the parent
			// drops the stale checklist, refreshes, and explains what happened.
			if (isEntityGoneError(error)) {
				console.warn("InlineChecklistEditor.save: the checklist is already gone on the server — handing off to the parent", {
					checklistId: props.checklistId,
					httpStatus: getApiErrorStatus(error)
				});
				emit("gone", error);
				return false;
			}
			console.error(error, {
				extra: {
					file: "InlineChecklistEditor.vue",
					method: "save"
				},
				tags: {
					checklistId: props.checklistId
				}
			});
			showSaveErrorToast(
				getMessageFromError(error) || "We're sorry, but we were unable to update the checklist name. Please try again."
			);
			return false;
		} finally {
			isSaving.value = false;
		}
	}

	defineExpose({ save });

	function cancel() {
		emit("cancel");
	}

	nextTick(() => {
		nameInput.value?.focus();
	});
</script>

<style scoped></style>
